import type { FC } from 'react'

import styled from '@emotion/styled'

import { useTheme } from '@emotion/react'

import Image from '@segment/matcha/components/Image'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'

import FormSubmitAction from '@segment/templates/FormSubmitAction'

import EmailFormSection from 'views/Academy/components/EmailFormSection'

import LessonCard from 'views/Academy/components/LessonCard'

import type { Lesson } from '@segment/api/routes/academy/types'

const copy = {
  heading: `Next lesson`,
  completed: {
    heading: `Congratulations!`,
    description: `You’ve completed the last lesson in this course. If you’re ready to keep learning you can start the next course below.`,
  },
}

interface NextLessonProp {
  data: Lesson
  courseNumber: number
  courseIcon: Lesson[`courseIcon`]
}

const NextLesson: FC<NextLessonProp> = ({ data, courseNumber, courseIcon }) => {
  const theme = useTheme()

  return (
    <Wrapper>
      <StyledSection variant="centered">
        <Section.Copy>
          <CopyTextBase centered={true}>
            {courseNumber === data.courseNumber ? (
              <>
                <Overline variant="textBoldSmall" tokens={{ color: theme.palette.twilio.gray[100] }}>
                  {courseIcon && <CourseIcon width="24" height="24" src={courseIcon?.src} alt={courseIcon?.title} />}

                  {data.courseTitle}
                </Overline>
                <CopyText.Heading tag="h3" variant="h2">
                  {copy.heading}
                </CopyText.Heading>
              </>
            ) : (
              <>
                <CourseCompleted
                  status="success"
                  heading={copy.completed.heading}
                  description={copy.completed.description}
                />
                <CopyText.Overline>
                  {data.courseIcon && (
                    <NextCourseIcon width="24" height="24" src={data.courseIcon?.src} alt={data.courseIcon?.title} />
                  )}
                </CopyText.Overline>
                <CopyText.Heading tag="h3" variant="h2">
                  Course: {data.courseNumber}: {data.courseTitle}
                </CopyText.Heading>
              </>
            )}
          </CopyTextBase>
        </Section.Copy>

        <Section.Contents>
          <LessonCard lesson={data} />
        </Section.Contents>

        <EmailFormSection />
      </StyledSection>
    </Wrapper>
  )
}

export default NextLesson

const Wrapper = styled.div`
  background-color: ${({ theme }) => theme.palette.twilio.gray[10]};
`

const StyledSection = styled(SectionBase)`
  max-width: 800px;

  ${Section.Wrapper} {
    gap: 40px;
  }
`

const CourseCompleted = styled(FormSubmitAction)`
  margin-bottom: 40px;
`

const Overline = styled(CopyText.Overline)`
  display: flex;
  gap: 6px;
  align-items: center;
  justify-content: center;
`

const CourseIcon = styled(Image)`
  width: 24px;
  margin: 0;
`

const NextCourseIcon = styled(CourseIcon)`
  width: 36px;
  height: 36px;
  margin: 0 auto;
`
